import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonBase, IconButton, Modal } from '@mui/material';

import { Text } from '~/components/ui/text';
import { COLORS } from '~/styles/colors';

const steps = [
  'builder:testScan.howToTest.step1',
  'builder:testScan.howToTest.step2',
  'builder:testScan.howToTest.step3',
  'builder:testScan.howToTest.step4',
];

type TestScanProps = {
  qrImage?: string;
};

export default function TestScan({ qrImage }: TestScanProps) {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const styles = useStyles();

  const handleClose = () => setIsModalOpen(false);

  return (
    <>
      <Box sx={styles.testScanWrapper}>
        <ButtonBase onClick={() => setIsModalOpen(true)} sx={styles.testScan}>
          {t('builder:testScan.title')}
        </ButtonBase>
      </Box>
      {isModalOpen && (
        <Modal
          open={isModalOpen}
          sx={styles.modal}
          slotProps={{
            backdrop: {
              sx: styles.backdrop,
            },
          }}
        >
          <Box sx={styles.container}>
            <IconButton onClick={handleClose} sx={styles.closeIconWrapper}>
              <CloseIcon sx={styles.closeIcon} />
            </IconButton>
            <Box sx={styles.qrImageContainer}>{qrImage && <img src={qrImage} alt="qr image" />}</Box>

            <Text variant="h2" sx={styles.title}>
              {t('builder:testScan.testCode')}
            </Text>

            <Text variant="input15" sx={styles.description}>
              {t('builder:testScan.description')}
            </Text>

            <Text variant="input15" sx={{ color: COLORS.grey400, textAlign: 'center' }}>
              {t('builder:testScan.howToTest.title')}
            </Text>
            {steps.map((step, index) => {
              return (
                <Text variant="input15" sx={{ marginTop: '8px', color: COLORS.grey400 }}>
                  {index + 1}. {t(step)}
                </Text>
              );
            })}
            <ButtonBase sx={styles.bottomClose} onClick={handleClose} disableRipple>
              {t('buttons:close')}
            </ButtonBase>
          </Box>
        </Modal>
      )}
    </>
  );
}

function useStyles() {
  return {
    testScanWrapper: css`
      display: flex;
      width: 100%;
      justify-content: center;
    `,
    testScan: css`
      margin-top: 12px;
      border: 1px solid ${COLORS.textMain};
      border-radius: 20px;
      padding: 4px 36px;
      font-weight: 500;
      font-size: 18px;
      color: ${COLORS.textMain};
    `,
    backdrop: css`
      background-color: #c9c8c84d;
      backdrop-filter: blur(3px);
    `,
    modal: css`
      display: flex;
      align-items: center;
      justify-content: center;
    `,
    closeIcon: css`
      color: ${COLORS.textMain};
    `,
    closeIconWrapper: css`
      margin-left: auto;
    `,
    container: css`
      width: 100%;
      max-width: 475px;
      padding: 20px 32px 40px;
      border-radius: 20px;
      background-color: ${COLORS.white};
      display: flex;
      flex-direction: column;
    `,
    title: css`
      cursor: initial;
      text-align: center;
      margin-top: 32px;
      margin-bottom: 8px;
    `,
    bottomClose: css`
      height: 40px;
      padding: 4px 20px;
      box-sizing: border-box;
      font-weight: 500;
      font-size: 18px;
      color: ${COLORS.textMain};
      width: fit-content;
      margin: 24px auto 0;
    `,
    description: css`
      cursor: initial;
      text-align: center;
      margin-bottom: 20px;
    `,
    howToTest: css`
      cursor: initial;
      text-align: center;
    `,
    qrImageContainer: css`
      width: 285px;
      min-height: 242px;
      padding: 6px 27px;
      border-radius: 20px;
      border: 1px solid ${COLORS.grey200};
      margin: 0 auto;

      img {
        max-width: 100%;
      }
    `,
  };
}
